import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { api, fileUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Truck, Package, MapPin, ArrowLeft, RefreshCw } from "lucide-react";
import { formatMoney } from "@/lib/currency";

const STATES = ["paid", "packed", "shipped", "delivered", "completed"];

export default function AdminOrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { settings } = useSettings();
  const [o, setO] = useState(null);
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [returning, setReturning] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const carriers = settings?.shipping_carriers || ["DHL Express", "FedEx", "UPS"];

  const load = () => api.get(`/orders/${id}`).then((r) => {
    setO(r.data);
    setCarrier(r.data.carrier || "");
    setTracking(r.data.tracking_number || "");
    setTrackingUrl(r.data.tracking_url || "");
  });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const updateStatus = async (status) => {
    try {
      await api.put(`/orders/${id}`, { status });
      toast.success(`marked ${status}.`);
      load();
    } catch { toast.error("update failed."); }
  };

  const saveTracking = async (notify) => {
    try {
      await api.put(`/orders/${id}`, { carrier, tracking_number: tracking, tracking_url: trackingUrl, status: o.status === "paid" ? "shipped" : o.status, notify_buyer: !!notify });
      toast.success(notify ? "tracking saved · buyer notified." : "tracking saved.");
      load();
    } catch { toast.error("save failed."); }
  };

  const submitReturn = async () => {
    if (!returnReason.trim()) return toast.error("provide a reason.");
    try {
      await api.post("/returns", { order_id: id, reason: returnReason });
      toast.success("return opened.");
      setReturning(false);
      setReturnReason("");
      load();
    } catch { toast.error("could not open return."); }
  };

  if (!o) return <AdminLayout><p className="editorial-label">loading…</p></AdminLayout>;

  return (
    <AdminLayout>
      <div data-testid="admin-order-detail" className="max-w-5xl">
        <button onClick={() => nav("/admin/orders")} className="editorial-label hover:text-brass mb-4 flex items-center gap-2"><ArrowLeft className="w-3 h-3" /> back to orders</button>
        <span className="editorial-label">order · {o.order_number}</span>
        <h1 className="font-serif text-5xl lowercase text-charcoal mt-2 mb-2">{o.item_snapshot?.title?.toLowerCase()}</h1>
        <p className="text-espresso mb-10">placed {new Date(o.created_at).toLocaleString()} · channel {o.channel}</p>

        {/* Status pipeline */}
        <div className="flex gap-2 mb-10 overflow-x-auto" data-testid="order-status-pipeline">
          {STATES.map((s, idx) => {
            const active = STATES.indexOf(o.status) >= idx;
            return (
              <button key={s} onClick={() => updateStatus(s)} data-testid={`set-${s}`} className={`flex-1 min-w-[110px] py-3 px-3 text-[0.65rem] tracking-[0.25em] uppercase border-l-2 ${active ? "border-brass bg-brass/10 text-brass" : "border-taupe text-taupe hover:text-charcoal"}`}>
                <span className="block">{String(idx + 1).padStart(2, "0")}</span>
                <span>{s}</span>
              </button>
            );
          })}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Buyer */}
          <section className="bg-linen border border-taupe/50 p-6">
            <p className="editorial-label mb-3 flex items-center gap-2"><MapPin className="w-3 h-3" /> ship to</p>
            <p className="font-serif text-xl lowercase text-charcoal">{o.buyer_name}</p>
            <p className="text-sm text-espresso mt-1">{o.buyer_email}</p>
            {o.buyer_phone && <p className="text-sm text-espresso">{o.buyer_phone}</p>}
            <p className="text-sm text-charcoal mt-3 leading-relaxed">{o.address_line}<br/>{o.city} {o.postal_code}<br/><span className="text-xs uppercase tracking-widest text-brass">{o.country}</span></p>
          </section>

          {/* Pricing */}
          <section className="bg-linen border border-taupe/50 p-6" data-testid="order-pricing">
            <p className="editorial-label mb-4 flex items-center gap-2"><Package className="w-3 h-3" /> pricing</p>
            <Row label="subtotal" value={o.subtotal} />
            <Row label="shipping" value={o.shipping_fee} />
            <Row label={`vat · ${o.country} ${o.vat_rate}%`} value={o.vat_amount} />
            <div className="border-t border-taupe/40 mt-3 pt-3"><Row label="total" value={o.total} bold accent /></div>
          </section>
        </div>

        {/* Tracking */}
        <section className="bg-linen border border-taupe/50 p-6 mt-6" data-testid="order-tracking">
          <p className="editorial-label mb-4 flex items-center gap-2"><Truck className="w-3 h-3" /> shipping & tracking</p>
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="editorial-label">carrier</label>
              <select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="track-carrier" className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none text-charcoal">
                <option value="">choose…</option>
                {carriers.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><label className="editorial-label">tracking number</label><input value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="track-number" className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" /></div>
            <div><label className="editorial-label">tracking url (optional)</label><input value={trackingUrl} onChange={(e) => setTrackingUrl(e.target.value)} data-testid="track-url" placeholder="https://…" className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" /></div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => saveTracking(false)} data-testid="track-save" className="border border-brass text-brass hover:bg-brass hover:text-ivory px-5 py-2 text-xs tracking-[0.25em] uppercase">save</button>
            <button onClick={() => saveTracking(true)} data-testid="track-notify" className="bg-brass hover:bg-espresso text-ivory px-5 py-2 text-xs tracking-[0.25em] uppercase">save & notify buyer</button>
          </div>
        </section>

        {/* Return */}
        <section className="bg-linen border border-taupe/50 p-6 mt-6" data-testid="order-return">
          <p className="editorial-label mb-4 flex items-center gap-2"><RefreshCw className="w-3 h-3" /> returns</p>
          {!returning ? (
            <button onClick={() => setReturning(true)} data-testid="open-return" className="border border-taupe hover:border-red-700 text-charcoal hover:text-red-700 px-5 py-2 text-xs tracking-[0.25em] uppercase">open a return</button>
          ) : (
            <div className="space-y-3">
              <textarea value={returnReason} onChange={(e) => setReturnReason(e.target.value)} placeholder="reason for return…" rows={2} data-testid="return-reason" className="w-full bg-ivory border border-taupe py-2 px-3 focus:outline-none text-charcoal" />
              <div className="flex gap-3">
                <button onClick={() => setReturning(false)} className="border border-taupe text-charcoal px-5 py-2 text-xs tracking-[0.25em] uppercase">cancel</button>
                <button onClick={submitReturn} data-testid="submit-return" className="bg-red-700 hover:bg-red-800 text-ivory px-5 py-2 text-xs tracking-[0.25em] uppercase">register return</button>
              </div>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}

function Row({ label, value, bold, accent }) {
  return (
    <div className={`flex justify-between py-1 ${bold ? "font-medium" : ""} ${accent ? "text-brass" : "text-charcoal"}`}>
      <span className="text-sm lowercase">{label}</span>
      <span className="text-sm tabular-nums">{formatMoney(value || 0)}</span>
    </div>
  );
}
